import { useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon, UserMinusIcon } from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  served: { label: 'Attended', icon: CheckCircleIcon, className: 'text-success' },
  missed: { label: 'Missed', icon: ExclamationTriangleIcon, className: 'text-error' },
  late: { label: 'Late', icon: ClockIcon, className: 'text-warning' },
  absent: { label: 'Absent', icon: UserMinusIcon, className: 'text-info' },
};

/**
 * Modal showing a session report preview before saving.
 */
export default function ConcludeSessionModal({
  isOpen,
  onClose,
  sessionActions,
  sessionSummary,
  conductedBy,
  consequenceTypeLabel,
  sessionComplete = false,
  remainingCount = 0,
  requireComplete = false,
  onConfirm,
  saving,
  integrityIssues = [],
}) {
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  // Group actions by status
  const grouped = {};
  sessionActions.forEach((action, eventId) => {
    const status = action.status;
    if (!grouped[status]) grouped[status] = [];
    grouped[status].push({ eventId, ...action });
  });

  // Order: served, late, missed, absent, other
  const statusOrder = ['served', 'late', 'missed', 'absent'];
  const orderedStatuses = statusOrder.filter(s => grouped[s]?.length > 0);

  const handleConfirm = () => {
    onConfirm(notes);
  };

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-1">
          {sessionComplete ? 'Session complete' : 'Conclude Session'}
        </h3>
        <p className="text-sm text-base-content/60 mb-4">
          {new Date().toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {consequenceTypeLabel && <> &middot; {consequenceTypeLabel}</>}
          {conductedBy && <> &middot; {conductedBy}</>}
        </p>
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {sessionSummary.attended > 0 && (
            <span className="badge badge-success gap-1">
              <CheckCircleIcon className="w-3.5 h-3.5" /> {sessionSummary.attended} Attended
            </span>
          )}
          {sessionSummary.late > 0 && (
            <span className="badge badge-warning gap-1">
              <ClockIcon className="w-3.5 h-3.5" /> {sessionSummary.late} Late
            </span>
          )}
          {sessionSummary.missed > 0 && (
            <span className="badge badge-error gap-1">
              <ExclamationTriangleIcon className="w-3.5 h-3.5" /> {sessionSummary.missed} Missed
            </span>
          )}
          {sessionSummary.absent > 0 && (
            <span className="badge badge-info gap-1">
              <UserMinusIcon className="w-3.5 h-3.5" /> {sessionSummary.absent} Absent
            </span>
          )}
        </div>

        {remainingCount > 0 && (
          <div className="alert alert-warning mb-4 text-sm" role="alert">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span>
              {requireComplete ? (
                <>
                  {remainingCount} {remainingCount === 1 ? 'learner is' : 'learners are'} still unmarked.
                  {' This session must remain open until every outcome is recorded.'}
                </>
              ) : (
                <>
                  {remainingCount} {remainingCount === 1 ? 'consequence has' : 'consequences have'} not been actioned.
                  {` ${remainingCount === 1 ? 'It' : 'They'} will remain waiting for action.`}
                </>
              )}
            </span>
          </div>
        )}

        {integrityIssues.length > 0 && (
          <div className="alert alert-error mb-4 items-start" role="alert" data-testid="session-integrity-issues">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold">Session not concluded</p>
              <p className="text-sm mt-0.5">
                The report did not match the saved Queue records. The session is still open.
              </p>
              <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                {integrityIssues.map(issue => (
                  <li key={issue.id}>
                    <span className="font-medium">{issue.studentName || issue.title}:</span>{' '}
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Grouped student list */}
        <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
          {orderedStatuses.map(status => {
            const config = STATUS_CONFIG[status] || { label: status, className: 'text-base-content' };
            const Icon = config.icon;
            const students = grouped[status];

            return (
              <div key={status}>
                <div className={`flex items-center gap-1.5 mb-1 ${config.className}`}>
                  {Icon && <Icon className="w-4 h-4" />}
                  <span className="text-sm font-medium">{config.label} ({students.length})</span>
                </div>
                <ul className="ml-6 space-y-0.5">
                  {students.map(s => (
                    <li key={s.eventId} className="text-sm text-base-content/80">
                      {s.studentName}
                      {s.className && <span className="text-base-content/50"> &middot; {s.className}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="flex flex-col gap-1.5">
          <label className="label">
            <span className="label-text text-sm">Notes (optional)</span>
          </label>
          <textarea
            className="textarea textarea-bordered textarea-sm"
            rows={2}
            placeholder="Any notes about this session..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary gap-2"
            onClick={handleConfirm}
            disabled={saving || (requireComplete && remainingCount > 0)}
          >
            {saving ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <CheckCircleIcon className="w-4 h-4" />
            )}
            {integrityIssues.length > 0 ? 'Recheck & Conclude' : 'Save Report & Conclude'}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop" onClick={onClose}>
        <button>close</button>
      </form>
    </dialog>
  );
}
